import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ThemaCode } from "reedy-data/api";
import { ThemaCodeType } from "reedy-data/api";
import {
  DialogDescription,
  DialogTitle,
  DialogHeader,
  Dialog,
  DialogContent,
  DialogOverlay,
} from "~/components/ui/dialog";
import { useAnalytics } from "~/contexts/analytics-context";
import { useProduct } from "~/contexts/product-context";
import {
  mapOrganisationLanguageToThema,
  mapProductLanguageToThema,
} from "~/utils/thema-language-mapping";
import { SearchType, Tab } from "~/shared/types/thema";
import { useSession } from "~/contexts/auth-context";
import {
  ThemaCodesSearchControls,
  ThemaCodesTabs,
} from "~/components/thema-codes";
import { useDebounce } from "~/hooks/use-debounce";
import { ModalFooter } from "./components";

interface SearchThemaCodesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SearchThemaCodesModal = ({
  open,
  onOpenChange,
}: SearchThemaCodesModalProps) => {
  const { t } = useTranslation(["productDetails", "general"]);
  const { gaEvent } = useAnalytics();
  const { product } = useProduct();

  // Modal state management
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState<SearchType>(SearchType.QUICK);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.SUBJECTS);
  const [selected, setSelected] = useState<ThemaCode[]>([]);
  const [showNotesInline, setShowNotesInline] = useState(false);
  const [notesManuallyDisabled, setNotesManuallyDisabled] = useState(false);
  const { organisation } = useSession();
  const debouncedQuery = useDebounce(query, 300);

  const resetModalState = useCallback(() => {
    setQuery("");
    setSelected([]);
    setActiveTab(Tab.SUBJECTS);
    setShowNotesInline(false);
    setNotesManuallyDisabled(false);
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      // resets editable modal state when the external open prop closes the modal
      resetModalState();
      return;
    }

    if (product) {
      gaEvent({
        type: "open_thema_search_modal",
        payload: {
          book: product.id,
          item_id: product.id,
          item_name: product.title || "Unknown",
        },
      });
    }
  }, [open, resetModalState, gaEvent, product]);

  // Extract and map language from product context
  const organisationThemaLanguage = organisation.effectiveLanguage
    ? mapOrganisationLanguageToThema(organisation.effectiveLanguage)
    : null;
  const productThemaLanguage = product?.language
    ? mapProductLanguageToThema(product.language)
    : null;

  const themaLanguage =
    productThemaLanguage || organisationThemaLanguage || "en";

  // Auto-enable notes when search is executed (unless manually disabled)
  useEffect(() => {
    if (debouncedQuery.trim() && !notesManuallyDisabled) {
      // auto-enables inline notes once a debounced search runs, unless the user manually disabled them; showNotesInline is also user-toggleable, so it cannot be derived
      setShowNotesInline(true);
    }
  }, [debouncedQuery, notesManuallyDisabled]);

  // Reset manual disable flag when search is completely cleared
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      // clears the manual-disable flag once the debounced search query is emptied; reacts to the debounced async value
      setNotesManuallyDisabled(false);
    }
  }, [debouncedQuery]);

  useEffect(() => {
    if (debouncedQuery.trim() && product) {
      gaEvent({
        type: "search_thema_codes",
        payload: {
          book: product.id,
          query: debouncedQuery,
          search_type: searchType,
          code_type:
            activeTab === Tab.SUBJECTS
              ? ThemaCodeType.SUBJECT
              : ThemaCodeType.QUALIFIER,
          tab: activeTab,
        },
      });
    }
  }, [debouncedQuery, searchType, activeTab, product, gaEvent]);

  const toggleSelect = (code: ThemaCode) => {
    const isCurrentlySelected = selected.some((c) => c.id === code.id);
    const action = isCurrentlySelected ? "deselect" : "select";

    if (product) {
      gaEvent({
        type: "select_thema_code",
        payload: {
          book: product.id,
          code_id: code.id,
          code: code.code,
          code_description: code.description || "Unknown",
          code_type:
            activeTab === Tab.SUBJECTS
              ? ThemaCodeType.SUBJECT
              : ThemaCodeType.QUALIFIER,
          action,
          tab: activeTab,
        },
      });
    }

    setSelected((prev) => {
      if (prev.some((c) => c.id === code.id)) {
        return prev.filter((c) => c.id !== code.id);
      }
      return [...prev, code];
    });
  };

  // Simple wrapper for StyledTabs (general-purpose component)
  const handleTabChange = (value: string) => {
    const fromTab = activeTab;
    const toTab = value as Tab;

    if (product && fromTab !== toTab) {
      gaEvent({
        type: "thema_tab_change",
        payload: {
          book: product.id,
          from_tab: fromTab,
          to_tab: toTab,
        },
      });
    }

    setActiveTab(toTab);
  };

  const handleToggleNotes = () => {
    const newState = !showNotesInline;
    setShowNotesInline(newState);
    // Track if user manually disabled notes during an active search
    if (!newState && debouncedQuery.trim()) {
      setNotesManuallyDisabled(true);
    } else if (newState) {
      // Reset manual disable flag when user re-enables notes
      setNotesManuallyDisabled(false);
    }
  };

  const handleDrillDownWithQueryClear = (
    code: ThemaCode,
    navigationStack: ThemaCode[],
  ) => {
    if (product) {
      gaEvent({
        type: "thema_code_drill_down",
        payload: {
          book: product.id,
          parent_code: code.code,
          parent_description: code.description || "Unknown",
          code_type:
            activeTab === Tab.SUBJECTS
              ? ThemaCodeType.SUBJECT
              : ThemaCodeType.QUALIFIER,
          navigation_level: navigationStack.length + 1,
        },
      });
    }

    // Clear search query when drilling down
    setQuery("");
  };

  const handleCloseModal = (isOpen: boolean) => {
    if (!isOpen && product) {
      gaEvent({
        type: "cancel_thema_search_modal",
        payload: {
          book: product.id,
          item_id: product.id,
          item_name: product.title || "Unknown",
          selected_codes_count: selected.length,
        },
      });
    }

    onOpenChange(isOpen);
  };

  const handleSuccess = () => {
    if (product && selected.length > 0) {
      const subjectsCount = selected.filter(
        (code) => code.type === ThemaCodeType.SUBJECT,
      ).length;
      const qualifiersCount = selected.filter(
        (code) => code.type === ThemaCodeType.QUALIFIER,
      ).length;

      gaEvent({
        type: "add_thema_codes_success",
        payload: {
          book: product.id,
          codes_count: selected.length,
          subjects_count: subjectsCount,
          qualifiers_count: qualifiersCount,
        },
      });
    }

    resetModalState();
  };

  return (
    <Dialog open={open} onOpenChange={handleCloseModal}>
      <DialogOverlay className="backdrop-blur-sm bg-black/10" />
      <DialogContent
        className="min-w-[95vw] max-w-[95vw] h-[95vh] flex flex-col"
        data-testid="add-thema-codes-modal"
      >
        <DialogHeader className="items-start space-y-0 my-0 mb-8 flex-shrink-0 overflow-hidden">
          <DialogTitle className="pb-1">
            {t("thema.searchThemaModal.title")}
          </DialogTitle>
          <DialogDescription className="text-md text-secondary-400 font-light">
            {t("thema.searchThemaModal.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col flex-1 overflow-hidden">
          <ThemaCodesSearchControls
            searchType={searchType}
            onSearchTypeChange={setSearchType}
            query={query}
            canSelect
            onQueryChange={setQuery}
            selected={selected}
            onRemoveSelected={toggleSelect}
            showNotesInline={showNotesInline}
            onToggleNotesInline={handleToggleNotes}
          />

          <ThemaCodesTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            query={debouncedQuery}
            selected={selected}
            themaLanguage={themaLanguage as "en" | "de"}
            onToggleSelect={toggleSelect}
            showNotesInline={showNotesInline}
            onDrillDown={handleDrillDownWithQueryClear}
          />
        </div>

        <ModalFooter
          selected={selected}
          onClose={() => handleCloseModal(false)}
          onSuccess={handleSuccess}
        />
      </DialogContent>
    </Dialog>
  );
};
